import { CohortQueryObject } from 'interfaces/cohort';
import {
  PrepareCustomerResultsJob,
  PrepareCustomerResultsParams,
  QueryStatistics,
  ResultForExport,
} from 'interfaces/job';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';
import { normalizeKeysToCamelCase } from 'utils/objectCase';

export const prepareCustomerResults = ({
  studyId,
  resultTypesToExport,
  jobId,
  queryObject,
  labId,
  zipGranularity,
  generateFeaturesCsvFile,
  publishResultsToCustomer,
  unitsPerZip,
  shardLimit,
}: {
  studyId: string;
  jobId?: string;
  labId: string;
} & Omit<
  PrepareCustomerResultsParams,
  'canViewInternalLabels' | 'snapshot' | 'queryStatistics' | 'unpublishedExperimentResultIds'
>) => {
  if (!studyId) {
    console.error('Study ID is required');
    return Promise.reject('Study ID is required');
  }
  return apiRequestHandlerPromise({
    url: `customer_results/${studyId}/prepare?${stringify(
      { resultTypesToExport, jobId, labId },
      {
        arrayFormat: 'repeat',
      }
    )}`,
    method: 'POST',
    data: JSON.stringify({
      queryObject,
      zipGranularity,
      generateFeaturesCsvFile,
      publishResultsToCustomer,
      unitsPerZip,
      shardLimit,
    }),
  });
};

export interface PrepareCustomerResultsJobWithUnpublishedExperimentResultIds extends PrepareCustomerResultsJob {
  unpublishedExperimentResultIds?: number[];
}

// The preview request uses the full case-filter payload from the study dashboard,
// including manual slide selections and resultsMode, not only the CohortQueryObject shape.
type PrepareCustomerResultsExportPreviewQueryObject = CasesParams;

const normalizePrepareCustomerResultsJob = <
  JobType extends PrepareCustomerResultsJobWithUnpublishedExperimentResultIds
>(
  job: JobType
): JobType => ({
  ...job,
  params: normalizeKeysToCamelCase(job.params),
});

export const getCustomerResultsJobs = (
  { studyId, labId }: { studyId: string; labId: string },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<PrepareCustomerResultsJobWithUnpublishedExperimentResultIds[]>({
    url: `customer_results/${studyId}/jobs?${stringify({ labId }, { arrayFormat: 'repeat' })}`,
    method: 'GET',
    signal,
  }).then((jobs) => jobs.map(normalizePrepareCustomerResultsJob));
};

export const previewPrepareCustomerResultsExport = (
  {
    studyId,
    queryObject,
    labId,
  }: {
    studyId: string;
    queryObject?: PrepareCustomerResultsExportPreviewQueryObject;
    labId: string;
  },
  signal: AbortSignal
) => {
  if (!studyId) {
    console.error('Study ID is required');
    return Promise.reject('Study ID is required');
  }
  return apiRequestHandlerPromise<{
    manifest: ResultForExport[];
    unpublishedExperimentResultIds: number[];
    queryStatistics: QueryStatistics;
  }>({
    url: `customer_results/${studyId}/prepare/preview_manifest?${stringify({ labId })}`,
    method: 'POST',
    data: queryObject ? JSON.stringify({ queryObject }) : undefined,
    signal,
  });
};

export const downloadSecondaryAnalysisPolygonsForStudy = ({
  studyId,
  labId,
  queryObject,
  signal,
}: {
  studyId: string;
  labId: string;
  queryObject?: CohortQueryObject;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise({
    url: `customer_results/${studyId}/download/secondary_analysis_polygons.get`,
    method: 'POST',
    mediaType: 'application/zip',
    responseType: 'blob',
    data: JSON.stringify({ queryObject, labId }),
    signal,
  });
};

export const getStudyCustomerResultsByJobId = (
  {
    studyId,
    jobId,
    labId,
  }: {
    studyId: string;
    jobId: string;
    labId: string;
  },
  signal?: AbortSignal
): Promise<string[]> => {
  if (!studyId) {
    console.error('Study ID is required');
    return Promise.reject('Study ID is required');
  }
  if (!jobId) {
    console.error('Job ID is required');
    return Promise.reject('Job ID is required');
  }
  return apiRequestHandlerPromise<string[]>({
    url: `customer_results/${studyId}/presigned_files/from_job/${jobId}?${stringify({ labId })}`,
    method: 'GET',
    signal,
  });
};

export const getLatestPerSlideStudyCustomerResults = (
  {
    studyId,
    labId,
  }: {
    studyId: string;
    labId: string;
  },
  signal?: AbortSignal
): Promise<string[]> => {
  if (!studyId) {
    console.error('Study ID is required');
    return Promise.reject('Study ID is required');
  }
  return apiRequestHandlerPromise<string[]>({
    url: `customer_results/${studyId}/presigned_files/latest_per_slide?${stringify({ labId })}`,
    method: 'GET',
    signal,
  });
};
